import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/motion/Reveal";
import { SOLUTIONS, IMAGES } from "@/data/content";

const EASE = [0.16, 1, 0.3, 1];

function Card({ item, index, className = "", children }) {
  const Icon = item.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, ease: EASE, delay: (index % 3) * 0.08 }}
      whileHover={{ y: -6 }}
      className={`group relative flex flex-col justify-between rounded-3xl border border-[#EEF1F4] bg-white p-8 lg:p-10 shadow-sm hover:shadow-xl hover:shadow-gray-200/60 transition-[box-shadow] duration-500 overflow-hidden ${className}`}
      data-testid={`solution-card-${item.id}`}
    >
      {children}
      <div>
        <div className="flex items-center justify-between">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#F5F6F8] text-[#374151] group-hover:bg-[#374151] group-hover:text-white transition-colors duration-500">
            <Icon className="h-5 w-5" />
          </span>
          <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#9CA3AF]">
            {item.tag}
          </span>
        </div>
        <h3 className="mt-6 font-display text-2xl lg:text-3xl font-bold tracking-tight text-[#1F2937]">
          {item.title}
        </h3>
        <p className="mt-3 text-sm lg:text-base leading-relaxed text-[#6B7280] max-w-md">
          {item.desc}
        </p>
      </div>
      <div className="mt-8 flex items-center gap-1.5 text-sm font-semibold text-[#374151]">
        Explore
        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </div>
    </motion.div>
  );
}

export default function Solutions() {
  return (
    <section
      id="solutions"
      className="relative py-24 lg:py-36 bg-white"
      data-testid="solutions-section"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
          <div>
            <Reveal>
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#9CA3AF]">
                Solutions
              </p>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-4 max-w-2xl font-display text-4xl lg:text-5xl font-extrabold tracking-tighter leading-[1.02] text-[#1F2937]">
                One intelligent platform for every conversation
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <p className="max-w-sm text-[#6B7280] leading-relaxed">
              From first message to full resolution — modular products that work
              beautifully together, or on their own.
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-6 lg:gap-8">
          {/* Feature card with image */}
          <Card
            item={SOLUTIONS[0]}
            index={0}
            className="md:col-span-8 lg:col-span-7 min-h-[360px]"
          >
            <div className="absolute inset-0 -z-0">
              <img
                src={IMAGES.support}
                alt=""
                className="h-full w-full object-cover opacity-[0.08] group-hover:opacity-[0.14] transition-opacity duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-white via-white/85 to-white/60" />
            </div>
          </Card>

          <Card
            item={SOLUTIONS[1]}
            index={1}
            className="md:col-span-8 lg:col-span-5 bg-[#F5F6F8] border-transparent"
          />
          <Card item={SOLUTIONS[2]} index={2} className="md:col-span-4 lg:col-span-4" />
          <Card item={SOLUTIONS[3]} index={3} className="md:col-span-4 lg:col-span-4" />
          <Card item={SOLUTIONS[4]} index={4} className="md:col-span-4 lg:col-span-4" />
          <Card
            item={SOLUTIONS[5]}
            index={5}
            className="md:col-span-8 lg:col-span-12 bg-[#EEF1F4] border-transparent"
          >
            <div className="absolute -right-10 -bottom-10 h-56 w-56 rounded-full bg-white/50 blur-2xl" />
          </Card>
        </div>
      </div>
    </section>
  );
}
